import { AxiosResponse } from 'axios';
import { format } from 'date-fns';
import qs from 'qs';
import { trackPromise } from 'react-promise-tracker';
import { api } from '../../../../../lib';
import { SuppliersExhibitionType } from '../../../../../store/useSuppliersExhibitionStore';
import { convertClusterParams } from '../../../../../utils/convertClusterParams';
import { getValidData } from '../../../RevisaoPrecos/utils';
import {
  campoDeIdentificadoresDoNivel,
  NivelDoUpload,
  tipoDeExibicaoDoNivel,
} from './components/Modals/CriarNegociacaoArquivo/nivelDoUpload';
import { resolveRowIdentity } from './NegociacaoFornecedor.rowIdentity';
import * as T from './NegociacaoFornecedor.types';
import type { DerivadosDaGravacao } from './derivadosDaGravacao';
import type { EscolhaDaMassa, ResultadoDaMassa } from './verbaEmMassa';
import type { EscolhasDaVerbaNoArquivo, LeituraPreviaDoArquivo } from './verbaNoArquivo';

export async function getDatapointsNegotiation(params: Partial<Record<string, any>>): Promise<T.SuppliersResponseProps> {
  return trackPromise(
    api({
      url: '/suppliers',
      method: 'GET',
      expectToken: true,
      params: convertClusterParams(
        getValidData({
          ...params,
        }),
      ),
      paramsSerializer(params) {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
    }).then(resp => resp.data),
    'get-datapoints-negociacao',
  );
}

export async function downloadDatapointsNegotiation(data: {
  filters: Record<string, string[]>;
  products: Record<'selectedIds' | 'excludedIds', string[]>;
  suppliersExhibitionType?: string,
  selectedAll: boolean;
  sort: string;
}) {
  const currentDate = new Date();
  const formattedDate = format(currentDate, 'dd-MM-yyyy');
  const downloadTableName = `${formattedDate}_ipa_negociacao_fornecedor.csv`;
  const payload = {
    ...data,
    filters: convertClusterParams(data.filters),
  };

  return trackPromise(
    api({
      url: '/suppliers/download',
      method: 'POST',
      expectToken: true,
      data: payload,
      responseType: 'arraybuffer',
    })
      .then(res => {
        if (res.status === 200) {
          return res.data;
        }
        return res;
      })
      .then(resp => {
        let download = resp;
        if (!download.status) {
          const blob = new Blob([download]);
          const hiddenElement = document.createElement('a');
          hiddenElement.href = window.URL.createObjectURL(blob);
          hiddenElement.target = '_blank';
          hiddenElement.download = downloadTableName;
          hiddenElement.click();
          download = null;
        }
        return download;
      }),
    'download-datapoints-negociacao',
  );
}

/** Chave derivada AUSENTE é informação, e não falta dela -- ver `derivadosDaGravacao`. */
export type RespostaDaEdicaoDeLinha = Pick<
  T.NegotiationDataItemProps,
  'isEdited' | 'newCost' | 'newPrice' | 'objectiveMargin' | 'productsToBePricedId' | 'costIndicator'
> &
  DerivadosDaGravacao;

export async function patchNegotiationSaveEditedDatapoint(
  datapoint: T.NegotitationEditDatapointProps,
  suppliersExhibitionType?: SuppliersExhibitionType,
): Promise<RespostaDaEdicaoDeLinha> {
  const validData = getValidData(datapoint);

  // O filtro acima descarta nulo, e apagar a verba E mandar nulo: por isso os dois campos dela
  // entram DEPOIS dele, e por PRESENCA da chave da origem, nunca por ela ter valor.
  const mexeuNaVerba = Object.prototype.hasOwnProperty.call(datapoint, 'sellinAllowanceOrigin') && datapoint.sellinAllowanceOrigin !== undefined;

  const verba = mexeuNaVerba ? { sellinAllowance: datapoint.sellinAllowance ?? null, sellinAllowanceOrigin: datapoint.sellinAllowanceOrigin ?? null } : {};

  const payload = {
    ...validData,
    ...verba,
    ...(suppliersExhibitionType === 'PRODUCT_SUPPLIERS_CLUSTER' &&
      datapoint.cluster?.clusterId && {
        clusterId: datapoint.cluster.clusterId,
      }),
  };

  const params = new URLSearchParams();
  if (suppliersExhibitionType) {
    params.append('suppliersExhibitionType', suppliersExhibitionType);
  }

  const url = `/suppliers${params.toString() ? `?${params.toString()}` : ''}`;

  // O nome da área de carregamento tem de ser o MESMO que a grade desenha naquela linha, e qual campo
  // nomeia a linha em cada visão é decisão da função abaixo -- a mesma que a grade usa. A linha é
  // remontada porque o corpo da requisição chama de `id` o identificador do ponto de dado.
  const rowIdentity = resolveRowIdentity(
    { productsToBePricedId: datapoint.id, productId: datapoint.productId },
    suppliersExhibitionType,
  );

  return trackPromise(
    api({
      url,
      method: 'PATCH',
      expectToken: true,
      data: payload,
    }).then(resp => resp.data as unknown as RespostaDaEdicaoDeLinha),
    `negotiation-edit-datapoint-${rowIdentity}`,
  );
}

/** Um produto escolhido na visão rede, do jeito que o servidor o entende. */
export type NetworkProductSelection = {
  productId: string;
  newObjectiveMargin?: number;
  newCost?: number;
  newPrice?: number;
};

/**
 * A seleção da visão rede: os produtos escolhidos e, no caminho de "selecionar tudo", os que o
 * comprador desmarcou.
 */
export type NetworkDatapointSelection = {
  networkProducts: NetworkProductSelection[];
  excludedNetworkProductIds: string[];
};

/** A seleção que viaja no corpo de salvar e no de acrescentar produto a uma negociação salva. */
export type DatapointsSelectionData = Record<'excludedIds' | 'selectedIds', string[]> & Partial<NetworkDatapointSelection>;

export type SalvarNegociacaoData = {
  name: string;
  supplier: string;
  supplierId?: number;
  startDate: Date | string;
  endDate?: Date | string | null;
  filters: Record<string, any>;
  datapoints: DatapointsSelectionData;
  selectedAll: boolean;
  author?: string;
  suppliersExhibitionType: string;
};

/** O corpo que a criação de negociação devolve. */
export type SalvarNegociacaoResponseData = {
  id: string;
  /** Quantos produtos o comprador pediu para gravar. */
  requestedProductCount?: number;
  /** Quantos entraram de fato. Menor que o pedido quando um produto deixou de existir na base. */
  writtenProductCount?: number;
};

export async function salvarNegociacao(data: SalvarNegociacaoData): Promise<AxiosResponse<SalvarNegociacaoResponseData>> {
  const payload = {
    ...data,
    filters: convertClusterParams(data.filters as Record<string, any>),
  };

  return trackPromise(
    api({
      url: '/negotiations',
      method: 'POST',
      expectToken: true,
      data: JSON.stringify(getValidData(payload)),
    }),
    'negotiation-save-negotiation',
  );
}

type ResponseDataItem = Record<'id' | 'name', string>;

export async function getNegociacoesSalvas(search?: string, suppliersExhibitionType?: string): Promise<Record<'label' | 'value', string>[]> {
  return trackPromise<AxiosResponse<ResponseDataItem[]>>(
    api({
      url: '/negotiations/find',
      expectToken: true,
      params: { search, suppliersExhibitionType },
      paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
    }),
  ).then(res => {
    return res.data.map(({ id, name }) => ({
      label: name,
      value: id,
    }));
  });
}

export type AddProductToNegotiationProps = {
  negotiationId: string;
  filters: Record<string, any>;
  datapoints: DatapointsSelectionData;
  selectedAll: boolean;
};

export async function addProductToNegotiation(data: AddProductToNegotiationProps, suppliersExhibitionType?: string): Promise<AxiosResponse<unknown>> {
  const params = new URLSearchParams();
  if (suppliersExhibitionType) {
    params.append('suppliersExhibitionType', suppliersExhibitionType);
  }

  const payload = {
    ...data,
    filters: convertClusterParams(data.filters as Record<string, any>),
  };

  return trackPromise(
    api({
      url: `/negotiations/datapoints/add${params.toString() ? `?${params.toString()}` : ''}`,
      method: 'PUT',
      expectToken: true,
      data: JSON.stringify(getValidData(payload)),
    }),
    'negotiation/add-product-to-negotiation',
  );
}

/**
 * **A seleção é a MESMA de "adicionar à negociação salva"**, campo por campo: aqui negociação
 * ainda não existe, e "selecionar tudo" sob filtro não tem identificadores para listar.
 */
export type VerbaEmMassaNaBuscaPayload = EscolhaDaMassa & {
  filters: Record<string, any>;
  datapoints: DatapointsSelectionData;
  selectedAll: boolean;
};

/** A resposta é por item, e é ela que atualiza as células: a grade não é recarregada. */
export async function aplicarVerbaEmMassaNaBusca(
  data: VerbaEmMassaNaBuscaPayload,
  suppliersExhibitionType?: string,
): Promise<ResultadoDaMassa> {
  const params = new URLSearchParams();
  if (suppliersExhibitionType) {
    params.append('suppliersExhibitionType', suppliersExhibitionType);
  }

  const payload = {
    ...data,
    filters: convertClusterParams(data.filters as Record<string, any>),
  };

  return trackPromise(
    api({
      url: `/suppliers/sellin-allowance/bulk${params.toString() ? `?${params.toString()}` : ''}`,
      method: 'PUT',
      expectToken: true,
      data: JSON.stringify(getValidData(payload)),
    }).then(resp => resp.data as ResultadoDaMassa),
    'negotiation/sellin-allowance-bulk',
  );
}

export async function desfazerAlteracaoDePrecos(data: Partial<T.NegotiationDataItemProps>, suppliersExhibitionType?: string) {
  const payload = {
    ...data,
    ...(suppliersExhibitionType === 'PRODUCT_SUPPLIERS_CLUSTER' &&
      data.cluster?.clusterId && {
        clusterId: data.cluster.clusterId,
      }),
  };

  const params = new URLSearchParams();
  if (suppliersExhibitionType) {
    params.append('suppliersExhibitionType', suppliersExhibitionType);
  }

  const url = `suppliers/undo-datapoint-changes${params.toString() ? `?${params.toString()}` : ''}`;

  const res = await api.put<T.NegotiationDataItemProps>(url, payload, {
    expectToken: true,
  });
  return res.data;
}

export type IGetCompetitorsInfoSuppliers = {
  productId: string;
  // Ausente na linha de rede, que consolida todas as lojas e não tem uma para informar.
  storeId?: string;
  productFamilyId: string | null;
  competitorStoreId?: string[];
  clusterId?: string;
  // Informado apenas quando a tela quer a fotografia da concorrência gravada no fechamento da
  // negociação. Sem ele, o servidor responde com o mercado de hoje.
  negotiationId?: string;
};

export type CriarNegociacaoArquivoPayload = {
  name: string;
  supplier: string;
  startDate: Date | string;
  nivel: NivelDoUpload;
  selectedIds: string[];
  file: File;
  /** Ausente é envio válido: sem leitura do arquivo não houve escolha a mandar. */
  escolhasDaVerba?: EscolhasDaVerbaNoArquivo | null;
};

/** **Não grava nada**, e por isso o comprador pode trocar de arquivo quantas vezes precisar. */
export async function lerPreviaDoArquivo(file: File): Promise<LeituraPreviaDoArquivo> {
  const formData = new FormData();
  formData.append('file', file);

  // O rastreador de promessa perde o tipo da resposta do axios, e por isso o corpo é nomeado.
  const resposta = (await trackPromise(
    api({
      url: '/negotiations/upload/preview',
      method: 'POST',
      expectToken: true,
      data: formData,
    }),
    'negociacao-leitura-previa-do-arquivo',
  )) as AxiosResponse<LeituraPreviaDoArquivo>;

  return resposta.data;
}

export async function criarNegociacaoComArquivo(data: CriarNegociacaoArquivoPayload): Promise<AxiosResponse<{ id: string }>> {
  // O grao sai do mapa do nivel, e nao de uma comparacao com cluster. Era aqui que "nao e cluster"
  // significava "e loja", e com o terceiro nivel isso mandaria rede como loja.
  const campoDeIdentificadores = campoDeIdentificadoresDoNivel(data.nivel);

  const jsonData = {
    name: data.name,
    supplier: data.supplier,
    startDate: typeof data.startDate === 'string' ? data.startDate : format(data.startDate, 'yyyy-MM-dd'),
    suppliersExhibitionType: tipoDeExibicaoDoNivel(data.nivel),
    // No nivel de rede nenhum campo de localidade viaja: a negociacao e a rede toda, e lista vazia de
    // loja ou de cluster seria um recorte que ela nao tem.
    ...(campoDeIdentificadores ? { [campoDeIdentificadores]: data.selectedIds } : {}),
    ...(data.escolhasDaVerba ?? {}),
  };

  const formData = new FormData();
  formData.append('file', data.file);
  formData.append('data', new Blob([JSON.stringify(jsonData)], { type: 'application/json' }));

  return trackPromise(
    api({
      url: '/negotiations/upload',
      method: 'POST',
      expectToken: true,
      data: formData,
    }),
    'negotiation-criar-com-arquivo',
  );
}

export async function getCompetitorsInfoSuppliers(
  // A loja NÃO tem valor padrão. Um padrão aqui deixa de ser rede de proteção e passa a ser fonte de
  // número errado: quem esquecesse de informar a loja veria, calado, o detalhe de outra loja. Sem loja
  // a requisição sai sem o parâmetro, e o servidor responde ou recusa, que é observável.
  { productId = '', storeId, productFamilyId = '', competitorStoreId = [], clusterId, negotiationId }: IGetCompetitorsInfoSuppliers,
  loadingArea?: string,
): Promise<T.NegotiationCompetitorsInfoProps> {
  // Nesta versão do axios a chamada da instância devolve AxiosPromise<R>, ou seja, o corpo da
  // resposta é o segundo parâmetro de tipo, e não o primeiro. Informá-lo é o que substitui o
  // retorno implicitamente solto que esta função tinha.
  const response = await trackPromise(
    api<T.NegotiationCompetitorsInfoProps, T.NegotiationCompetitorsInfoProps>({
      url: `/competitor/prices/suppliers/${productId}`,
      method: 'GET',
      expectToken: true,
      // getValidData descarta os valores indefinidos, então um identificador ausente simplesmente
      // não aparece na string de consulta -- não é preciso ramificar a chamada aqui.
      params: getValidData({
        storeId,
        productFamilyId,
        competitorStoreId,
        clusterId,
        negotiationId,
      }),
      paramsSerializer(params) {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
    }),
    loadingArea,
  );

  return response.data;
}
